import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

// 신분증 가로세로 비율
const ID_CARD_ASPECT = 325 / 208;

// 페이지 애니메이션 설정
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

function StatusIdCardCropPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationNumber } = useParams();
  const [originalImage, setOriginalImage] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const cropWrapperRef = useRef(null);

  // 모바일 multi-touch(핀치 줌 등) 시 react-image-crop이 두 번째 손가락을
  // 새 selection 시작으로 인식해 crop 영역이 사라지는 이슈 방지
  // → capture phase에서 ReactCrop 도달 전에 multi-touch 이벤트를 막는다
  useEffect(() => {
    const wrapper = cropWrapperRef.current;
    if (!wrapper) return;

    const blockMultiTouch = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    wrapper.addEventListener("touchstart", blockMultiTouch, {
      capture: true,
      passive: false,
    });
    wrapper.addEventListener("touchmove", blockMultiTouch, {
      capture: true,
      passive: false,
    });

    return () => {
      wrapper.removeEventListener("touchstart", blockMultiTouch, {
        capture: true,
      });
      wrapper.removeEventListener("touchmove", blockMultiTouch, {
        capture: true,
      });
    };
  }, [originalImage]);

  // 이미지 로드 시 신분증 비율로 중앙 크롭 초기화
  // - completedCrop도 함께 세팅 → 사용자가 움직이지 않아도 완료 버튼이 작동
  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const newCrop = centerCrop(
      makeAspectCrop(
        { unit: "%", width: 90 },
        ID_CARD_ASPECT,
        width,
        height,
      ),
      width,
      height,
    );
    setCrop(newCrop);
    setCompletedCrop({
      unit: "px",
      x: (newCrop.x * width) / 100,
      y: (newCrop.y * height) / 100,
      width: (newCrop.width * width) / 100,
      height: (newCrop.height * height) / 100,
    });
  };

  useEffect(() => {
    // location.state에서 이미지 가져오기 또는 로컬 스토리지에서 가져오기
    const image = location.state?.originalImage;
    if (image) {
      setOriginalImage(image);
    } else {
      // 이미지가 없는 경우 업로드 페이지로 돌아가기
      navigate(`/status/id-card/${applicationNumber}`);
    }
  }, [location, navigate, applicationNumber]);

  // 크롭된 이미지 생성 함수
  const generateCroppedImage = () => {
    if (!completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = 325; // 최종 출력 너비
    canvas.height = 208; // 최종 출력 높이

    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return canvas.toDataURL("image/jpeg", 0.95);
  };

  // 완료 버튼 클릭 시
  const handleCropComplete = () => {
    const croppedImage = generateCroppedImage();
    if (croppedImage) {
      // 로컬 스토리지에 저장
      localStorage.setItem("temp_captured_image", croppedImage);

      // StatusIdCardSubmitPage로 이동
      navigate(`/status/id-card/${applicationNumber}`, {
        state: {
          fromCrop: true,
          capturedImage: { imageDataURL: croppedImage },
        },
      });
    }
  };

  // 취소 버튼 클릭 시
  const handleCancel = () => {
    navigate(`/status/id-card/${applicationNumber}`);
  };

  return (
    <motion.div
      className="crop-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="crop-header">
        <button className="crop-back-button" onClick={handleCancel}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1>신분증 영역 설정</h1>
        <button className="crop-close-button" onClick={handleCancel}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6L18 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="crop-container">
        <div className="crop-instructions">
          <p>신분증이 프레임 안에 완전히 들어오도록 조정해주세요</p>
        </div>

        {originalImage && (
          <div ref={cropWrapperRef} className="crop-wrapper">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={ID_CARD_ASPECT}
              keepSelection
              className="crop-image-container"
            >
              <img
                ref={imgRef}
                src={originalImage}
                alt="신분증 이미지"
                onLoad={onImageLoad}
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(100dvh - 220px)",
                  touchAction: "none",
                }}
              />
            </ReactCrop>
          </div>
        )}

        <canvas ref={previewCanvasRef} style={{ display: "none" }} />
      </div>

      <div className="crop-controls">
        <button className="crop-cancel-button" onClick={handleCancel}>
          취소
        </button>
        <button className="crop-complete-button" onClick={handleCropComplete}>
          완료
        </button>
      </div>

      <style jsx="true">{`
        .crop-page {
          display: flex;
          flex-direction: column;
          /* 모바일 브라우저 UI 변동까지 반영 (100vh 대비 정확) */
          height: 100dvh;
          background-color: #000;
          color: #fff;
        }

        .crop-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background-color: #000;
          position: relative;
          z-index: 10;
          flex-shrink: 0;
        }

        .crop-header h1 {
          font-size: 18px;
          margin: 0;
          text-align: center;
          flex-grow: 1;
        }

        .crop-back-button,
        .crop-close-button {
          background: none;
          border: none;
          cursor: pointer;
          color: white;
          padding: 5px;
        }

        .crop-container {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 10px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .crop-instructions {
          margin-bottom: 15px;
          text-align: center;
          color: #fff;
          font-size: 16px;
          flex-shrink: 0;
        }

        /* multi-touch 차단: 핀치 줌으로 인한 crop 영역 손실 방지 */
        .crop-wrapper {
          touch-action: none;
          -webkit-user-select: none;
          user-select: none;
        }

        /* 하단 버튼을 항상 화면에 고정 — 세로 긴 이미지에서도 보이도록 */
        .crop-controls {
          position: sticky;
          bottom: 0;
          display: flex;
          justify-content: space-between;
          padding: 15px 20px calc(15px + env(safe-area-inset-bottom, 0px))
            20px;
          background-color: #000;
          flex-shrink: 0;
          z-index: 20;
          box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.5);
        }

        .crop-cancel-button,
        .crop-complete-button {
          padding: 10px 20px;
          border-radius: 5px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          width: 48%;
        }

        .crop-cancel-button {
          background-color: #333;
          color: white;
          border: 1px solid #555;
        }

        .crop-complete-button {
          background-color: #E31C20;
          color: white;
          border: none;
        }

        .crop-image-container {
          max-width: 100%;
          max-height: 70vh;
          display: flex;
          justify-content: center;
        }
      `}</style>
    </motion.div>
  );
}

export default StatusIdCardCropPage;
